import type { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { Button } from "@sellar-io/components";
import { useState } from "react";
import { BasicDialog } from "../../../components/modal/BasicDialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useOrderFailedCollectionDecision } from "../../../hooks/api/v2/order/useOrderFailedCollectionDecision";
import { historyDate } from "../deliveryHistory/deliveryHistoryDates";

type Resolution = NonNullable<
  OrderGetResponseType["FailedCollectionResolution"]
>;
type Decision = "waitForOriginalOrder" | "cancellationRequested";

function getDecisionMessage(resolution: Resolution, supplierName: string) {
  if (resolution.state === "waitForOriginalOrder") {
    const acceptedDeliveryAt =
      resolution.acceptedDeliveryAt !== undefined
        ? resolution.acceptedDeliveryAt
        : resolution.expectedDeliveryAt;
    return acceptedDeliveryAt
      ? `You chose to wait for the order, with an estimated delivery date of ${historyDate(new Date(acceptedDeliveryAt))}.`
      : "You chose to wait for the order. Your estimated delivery date is to be confirmed.";
  }
  if (resolution.state === "cancellationRequested") {
    return "Your cancellation has not completed yet. If it remains pending, try cancelling again or contact Sellar.";
  }
  return `Nobody was available at ${supplierName} when our driver arrived, so we were unable to collect the stock. You can either cancel your order or choose to wait for another collection attempt.`;
}

export function FailedCollectionBuyerActionCard({
  orderId,
  supplierName,
  resolution,
  onViewDetails,
}: {
  orderId: number;
  supplierName: string;
  resolution: Resolution;
  onViewDetails: () => void;
}) {
  const mutation = useOrderFailedCollectionDecision(orderId);
  const [pendingDecision, setPendingDecision] = useState<Decision | null>(null);
  if (resolution.state === "replanned" || resolution.state === "cancelled") {
    return null;
  }
  const deliveryEstimate = resolution.expectedDeliveryAt
    ? `Your updated estimated delivery date is ${historyDate(new Date(resolution.expectedDeliveryAt))}.`
    : "Your updated delivery date is to be confirmed.";
  const errorMessage =
    apiV2AssertError(mutation.error)?.response?.data.message ??
    "We couldn’t save your decision. Please try again or contact Sellar.";
  return (
    <div className="space-y-3 rounded-lg border border-orange-200 bg-orange-50 p-3">
      <h3 className="text-sm font-semibold">
        Delivery delayed — decide what happens next
      </h3>
      <p className="text-sm">{getDecisionMessage(resolution, supplierName)}</p>
      {resolution.state === "pendingBuyerDecision" && (
        <p className="text-sm">{deliveryEstimate}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {resolution.state === "pendingBuyerDecision" && (
          <Button
            size="small"
            disabled={mutation.isPending}
            loading={
              mutation.isPending &&
              mutation.variables.state === "waitForOriginalOrder"
            }
            onClick={() => setPendingDecision("waitForOriginalOrder")}
          >
            Wait for my order
          </Button>
        )}
        <Button
          color="secondary"
          size="small"
          disabled={mutation.isPending}
          loading={
            mutation.isPending &&
            mutation.variables.state === "cancellationRequested"
          }
          onClick={() => setPendingDecision("cancellationRequested")}
        >
          Cancel my order
        </Button>
        <Button color="secondary" size="small" onClick={onViewDetails}>
          View details
        </Button>
      </div>
      {mutation.isError && (
        <p role="alert" className="text-sm text-red-600">
          {errorMessage}
        </p>
      )}
      <BasicDialog
        dialogActive={pendingDecision !== null}
        title={
          pendingDecision === "waitForOriginalOrder"
            ? "Confirm you wish to wait for your order"
            : "Confirm you wish to cancel your order"
        }
        loading={mutation.isPending}
        onDismiss={() => {
          if (!mutation.isPending) {
            setPendingDecision(null);
            mutation.reset();
          }
        }}
        onConfirm={() => {
          if (!pendingDecision || mutation.isPending) {
            return;
          }
          mutation.mutate(
            {
              state: pendingDecision,
              expectedDeliveryAt: resolution.expectedDeliveryAt
                ? new Date(resolution.expectedDeliveryAt).toISOString()
                : null,
            },
            { onSuccess: () => setPendingDecision(null) },
          );
        }}
      >
        <div className="flex flex-col gap-4 p-4 text-sm">
          <p>
            {pendingDecision === "waitForOriginalOrder"
              ? `The Sellar team will re-attempt collection from ${supplierName}. ${deliveryEstimate}`
              : `Your order will be cancelled immediately and ${supplierName} will be notified by email.`}
          </p>
          {mutation.isError && (
            <p role="alert" className="text-red-600">
              {errorMessage}
            </p>
          )}
        </div>
      </BasicDialog>
    </div>
  );
}
